/**
 * vCard 3.0 (RFC 2426) builder + downloader.
 *
 * Replaces vcards-js, which does not fold long content lines, emits an
 * invalid PHOTO TYPE parameter and does not escape text values.
 *
 * Delivery uses a Blob + <a download> instead of navigating to a data: URL,
 * because top-level navigation to data: URLs is blocked by modern browsers
 * (silently - no error, no download).
 */

const CRLF = "\r\n";
const MAX_OCTETS = 75;

const PHOTO_TYPES = {
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/jpg": "JPEG",
  "image/gif": "GIF",
  "image/webp": "WEBP",
  "image/bmp": "BMP",
  "image/tiff": "TIFF",
  "image/svg+xml": "SVG",
};

function encodeUtf8(str) {
  return new TextEncoder().encode(str);
}

/**
 * Escape a vCard text value: backslash, newline, comma and semicolon are
 * structural, so a company name like "TapFactor Sdn; Bhd" must be escaped
 * or it silently splits the record.
 */
function escapeText(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/**
 * Fold a content line at 75 octets, continuation lines prefixed with a space.
 * Counts UTF-8 bytes (not JS chars) and backs off so a multi-byte sequence is
 * never split mid-character.
 */
function foldLine(line) {
  const bytes = encodeUtf8(line);
  if (bytes.length <= MAX_OCTETS) return line;

  const decoder = new TextDecoder();
  const chunks = [];
  let start = 0;
  let limit = MAX_OCTETS;

  while (start < bytes.length) {
    let end = Math.min(start + limit, bytes.length);
    if (end < bytes.length) {
      // 0b10xxxxxx marks a UTF-8 continuation byte - walk back off it.
      while (end > start && (bytes[end] & 0xc0) === 0x80) end--;
      if (end === start) end = Math.min(start + limit, bytes.length);
      // Never break an escape sequence across the fold: a chunk ending in an
      // odd run of backslashes would strand "\" at end-of-line, which strict
      // parsers reject. Dropping one byte flips the run to even, and the
      // escape moves intact to the next line.
      let slashes = 0;
      while (end - 1 - slashes >= start && bytes[end - 1 - slashes] === 0x5c) {
        slashes++;
      }
      if (slashes % 2 === 1 && end - 1 > start) end--;
    }
    chunks.push(decoder.decode(bytes.subarray(start, end)));
    start = end;
    limit = MAX_OCTETS - 1; // continuation lines spend one octet on the space
  }

  return chunks.join(CRLF + " ");
}

function photoType(mime) {
  const key = String(mime || "").toLowerCase().trim();
  if (PHOTO_TYPES[key]) return PHOTO_TYPES[key];
  const subtype = key.split("/")[1];
  return subtype ? subtype.toUpperCase() : "JPEG";
}

/**
 * "Wei De Chang" -> { first: "Wei", last: "De Chang" }
 * FN carries the display name, so N only needs to be well-formed.
 */
function splitName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

function safeFilename(name) {
  const base = String(name || "contact")
    .trim()
    .replace(/[\\/:*?"<>|\r\n]+/g, "-")
    .replace(/\s+/g, "_")
    .replace(/^[.-]+|[.-]+$/g, "")
    .slice(0, 60);
  return (base || "contact") + ".vcf";
}

/**
 * Build a conformant vCard 3.0 string. Blank fields are omitted.
 *
 * @param {Object} data
 * @param {string} [data.name]
 * @param {string} [data.organization]
 * @param {string} [data.title]
 * @param {string} [data.cellPhone]
 * @param {string} [data.workPhone]
 * @param {string} [data.workFax]
 * @param {string} [data.email]
 * @param {string} [data.url]
 * @param {string} [data.note]
 * @param {{mime: string, base64: string}} [data.photo]
 */
export function buildVCard(data) {
  const d = data || {};
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];

  const { first, last } = splitName(d.name);
  lines.push("N:" + escapeText(last) + ";" + escapeText(first) + ";;;");
  if (d.name) lines.push("FN:" + escapeText(d.name));
  if (d.organization) lines.push("ORG:" + escapeText(d.organization));
  if (d.title) lines.push("TITLE:" + escapeText(d.title));
  if (d.cellPhone) lines.push("TEL;TYPE=CELL,VOICE:" + escapeText(d.cellPhone));
  if (d.workPhone) lines.push("TEL;TYPE=WORK,VOICE:" + escapeText(d.workPhone));
  if (d.workFax) lines.push("TEL;TYPE=WORK,FAX:" + escapeText(d.workFax));
  if (d.email) lines.push("EMAIL;TYPE=INTERNET,PREF:" + escapeText(d.email));
  if (d.url) lines.push("URL:" + escapeText(d.url));
  if (d.note) lines.push("NOTE:" + escapeText(d.note));
  if (d.photo && d.photo.base64) {
    lines.push(
      "PHOTO;ENCODING=b;TYPE=" + photoType(d.photo.mime) + ":" + d.photo.base64
    );
  }
  lines.push("END:VCARD");

  return lines.map(foldLine).join(CRLF) + CRLF;
}

/**
 * Fetch an image and return { mime, base64 }.
 * Returns null (and warns) on failure so a broken photo does not kill the
 * whole download.
 */
export async function fetchPhoto(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("HTTP " + response.status);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const match = /^data:([^;,]*);base64,(.*)$/.exec(String(dataUrl));
    if (!match || !match[2]) return null;
    return { mime: match[1] || blob.type || "image/jpeg", base64: match[2] };
  } catch (error) {
    console.warn("vCard: skipping profile photo -", error);
    return null;
  }
}

/**
 * Hand the browser a real file. A blob: URL is same-origin and carries a real
 * MIME type, so it is not subject to the data: URL navigation block.
 */
export function downloadVCard(vCardString, name) {
  const blob = new Blob([vCardString], { type: "text/vcard;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = objectUrl;
  anchor.download = safeFilename(name);
  anchor.rel = "noopener";
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

/**
 * Fetch the photo, build the card, hand it to the browser.
 * Pass the image URL as `photoUrl`; everything else matches buildVCard().
 */
export async function saveContactCard(data) {
  const d = data || {};
  const photo = await fetchPhoto(d.photoUrl);
  const vCardString = buildVCard(Object.assign({}, d, { photo }));
  downloadVCard(vCardString, d.name);
  return vCardString;
}

export default saveContactCard;
